/**
 * Etat de partie. Mis a jour UNIQUEMENT par les messages serveur : aucune
 * action utilisateur n'ecrit ici directement, elle envoie une intention et
 * attend le snapshot. C'est ce qui garantit que deux onglets ne divergent pas.
 *
 * Rien de haute frequence ici : traits, pointeur et timers vivent hors de React
 * (cf. CLAUDE.md). Le store ne bouge que sur evenement serveur, soit quelques
 * fois par seconde au pire.
 */
import { create } from 'zustand';
import {
  type CollabBoard,
  keepsAssignment,
  keepsGuessTarget,
  keepsHunt,
  type HuntTarget,
  type DrawZone,
  type GalleryItem,
  type RevealItem,
  type RoomSnapshot,
  type ServerMessage,
} from '@retusch/shared';
import { DRAWING_TAG, connection, type ConnectionStatus } from '../net/connection.js';
import type { TranslationKey } from '../locales/index.js';
import { useUiStore } from './uiStore.js';

export interface AssignmentState {
  readonly round: number;
  readonly file: string;
  readonly zone: DrawZone;
  readonly palette: readonly string[];
  /**
   * Mode collaboratif : les retouches DEJA posees sur ce tableau, dans
   * l'ordre. On dessine par-dessus, on ne les annule pas : ce sont celles des
   * autres.
   *
   * `expected` dit combien en attendre. Sans ce compte, le plateau se
   * monterait sur un fond incomplet et les couches apparaitraient une a une
   * sous les yeux du joueur, qui aurait deja commence a dessiner.
   */
  readonly expected: number;
  readonly layers: readonly ArrayBuffer[];
  /** La palette de chaque couche, dans le meme ordre. */
  readonly layerPalettes: readonly (readonly string[])[];
}

export interface GuessTargetState {
  readonly round: number;
  readonly drawerId: string;
  readonly file: string;
  readonly zone: DrawZone;
  readonly palette: readonly string[];
  readonly slot: number;
  /** Dessin recu en binaire, null tant que la trame n'est pas arrivee. */
  drawing: ArrayBuffer | null;
}

export interface GuessResultState {
  readonly found: boolean;
  readonly zone: DrawZone;
  readonly distance: number;
  readonly guesserPoints: number;
  readonly drawerPoints: number;
}

export interface RevealState {
  readonly round: number;
  readonly items: readonly RevealItem[];
  /** Dessins par slot, remplis au fil des trames binaires. */
  readonly drawings: Record<number, ArrayBuffer>;
}

/**
 * Galerie de fin. Les trames binaires arrivent apres le JSON et dans un ordre
 * non garanti : chaque item a donc son propre etat de chargement, et la grille
 * s'affiche des le JSON recu plutot que d'attendre le dernier blob.
 */
export interface GalleryState {
  readonly items: readonly GalleryItem[];
  readonly drawings: Record<number, ArrayBuffer>;
}

/**
 * Un toast porte une CLE de traduction, jamais un texte : le store n'a pas de
 * langue, c'est le rendu qui traduit. Changer de langue retraduit donc aussi
 * les toasts deja affiches.
 */
export interface ChatMessage {
  readonly id: number;
  readonly playerId: string;
  readonly nickname: string;
  readonly text: string;
  readonly at: number;
}

export interface ToastState {
  readonly id: number;
  readonly key: TranslationKey;
  readonly tone: 'error' | 'info';
  /** Substitutions du message, quand il en porte (nom, score). */
  readonly params?: Readonly<Record<string, string | number>>;
  /**
   * Lien sortant optionnel, affiche a la suite du message.
   *
   * Un seul usage aujourd'hui : « Ouvrir Discord » apres un partage reussi.
   * Sans lui, la bulle annonce que quelque chose est parti quelque part et
   * laisse le joueur chercher lui-meme ou c'est parti.
   */
  readonly link?: { readonly key: TranslationKey; readonly href: string };
}

interface RoomState {
  status: ConnectionStatus;
  playerId: string | null;
  room: RoomSnapshot | null;
  assignment: AssignmentState | null;
  guessTarget: GuessTargetState | null;
  /**
   * Refus du serveur alors qu'on n'est dans AUCUN salon : salon plein, disparu,
   * partie deja lancee. Sans cet etat, l'ecran de salon restait sur ses points
   * de suspension pour toujours, sans le moindre lien de sortie.
   */
  joinError: string | null;
  /**
   * Mode collaboratif : tout ce qu'il faut pour voter.
   *
   * `drawings` est indexe par le couple (tableau, auteur) encode dans un seul
   * entier, exactement comme l'enveloppe binaire : c'est la meme cle des deux
   * cotes du fil, il n'y a donc rien a apparier a la main.
   */
  collab: {
    readonly boards: readonly CollabBoard[];
    readonly drawings: Record<number, ArrayBuffer>;
    /** MES voix, par tableau : l'ordre de l'auteur choisi. */
    readonly mine: Record<number, number>;
    /** Depouillement, seulement a la revelation. */
    readonly tally: readonly (readonly number[])[] | null;
  } | null;
  /**
   * Chasse en cours (battle royale) : le tableau commun, toutes les retouches,
   * et celles deja trouvees. Survit a la revelation : c'est le meme ecran, fige.
   */
  hunt: {
    round: number;
    file: string;
    targets: readonly HuntTarget[];
    drawings: Record<number, ArrayBuffer>;
    found: Record<number, { drawerId: string; finderId: string }>;
    /**
     * Slots dans leur ORDRE DE DECOUVERTE.
     *
     * `found` ne peut pas le porter : ses cles sont des entiers, et un objet
     * JavaScript les range par valeur CROISSANTE, pas par ordre d'insertion.
     * L'ordre est pourtant le classement lui-meme en battle royale, ou le
     * premier trouve finit dernier. A la reconnexion, le serveur rejoue les
     * messages `found` dans le bon ordre, ce tableau se reconstruit donc seul.
     */
    foundOrder: readonly number[];
  } | null;
  /**
   * Mode concours : mon vote, et le depouillement.
   *
   * `mine` ne vient QUE du serveur (message `voted`), jamais d'un espoir local :
   * un vote refuse doit laisser l'ecran sur le choix qui compte vraiment.
   * `tally` reste null jusqu'a la revelation : afficher les voix pendant le vote
   * ferait suivre le troupeau.
   */
  contest: { mine: number | null; tally: Record<number, number> | null };
  /**
   * Dernier dessin rendu par CE joueur, avec le tableau qui l'accompagne.
   * Sert a le lui remontrer pendant la devinette : en mode « un seul dessine »
   * il n'a rien d'autre a faire que regarder les autres chercher sa retouche.
   */
  submitted: { round: number; file: string; zone: DrawZone; palette: readonly string[]; drawing: ArrayBuffer } | null;
  /**
   * Quelqu'un me regarde dessiner : mon plateau emet alors des instantanes.
   * Faux par defaut, et remis a faux des que la phase de dessin s'acheve.
   */
  watched: boolean;
  /**
   * Ce que je regarde, quand je n'ai pas de role cette manche. `file` a null
   * signifie que le joueur choisi n'a rien a montrer.
   */
  spectate: {
    readonly target: string | null;
    readonly round: number;
    readonly file: string | null;
    readonly zone: DrawZone | null;
    readonly palette: readonly string[];
    readonly slot: number;
    /** Dernier instantane recu de ce joueur. */
    readonly drawing: ArrayBuffer | null;
  } | null;
  guessResult: GuessResultState | null;
  reveal: RevealState | null;
  gallery: GalleryState | null;
  /** Historique de chat de la partie. Jamais persiste. */
  chat: readonly ChatMessage[];
  /** Reactions recues, consommees puis retirees par l'UI. */
  reactions: ReadonlyArray<{ id: number; playerId: string; emoji: string }>;
  toasts: readonly ToastState[];
  /** Le dessin a-t-il deja ete envoye pour la manche en cours ? */
  submittedRound: number;

  dismissToast(id: number): void;
  dropReaction(id: number): void;
  markSubmitted(round: number): void;
  rememberSubmission(submitted: RoomState['submitted']): void;
  reset(): void;
}

let nextId = 1;

/**
 * Bulles affichees simultanement, au plus. Au-dela, les plus ANCIENNES sortent.
 *
 * Une chasse commune a dix joueurs annonce une trouvaille par retouche, plus
 * les arrivees et les departs : la pile montait jusqu'a manger la moitie de
 * l'ecran, par-dessus le tableau qu'on est justement en train de fouiller.
 *
 * Le plafond est pose A LA SOURCE, pas au rendu. N'en afficher que trois en
 * gardant les autres en memoire ferait une FILE : chaque bulle expiree
 * laisserait monter la suivante, et la pile mettrait des dizaines de secondes
 * a se vider en montrant des evenements perimes. Ici, ce qui est chasse est
 * oublie.
 *
 * On sacrifie les anciennes et non les nouvelles : la derniere trouvaille est
 * celle qui vient de changer le jeu, la premiere a deja ete lue.
 */
const MAX_TOASTS = 3;

/** Codes serveur ayant un message dedie dans les catalogues. */
const KNOWN_ERROR_CODES = new Set([
  'ROOM_NOT_FOUND',
  'ROOM_FULL',
  'ROOM_IN_GAME',
  'NOT_HOST',
  'SETTINGS_LOCKED',
  'NO_CONTENT_FOR_CATEGORY',
  'CAP_BELOW_PLAYERS',
  'NOT_ENOUGH_PLAYERS',
  'WRONG_PHASE',
  'RATE_LIMIT',
  'DRAWING_TOO_LARGE',
  'BAD_MESSAGE',
  'ALREADY_DONE',
  'INTERNAL',
  'ROOM_RATE_LIMIT',
]);

/** Cle de traduction d'un code serveur. Exportee : l'ecran d'echec d'entree
 *  affiche la raison en clair plutot qu'un simple toast fugace. */
export function errorKey(code: string): TranslationKey {
  return (KNOWN_ERROR_CODES.has(code) ? `error.${code}` : 'error.unknown') as TranslationKey;
}

export const useRoomStore = create<RoomState>((set) => ({
  status: 'idle',
  playerId: null,
  room: null,
  assignment: null,
  guessTarget: null,
  guessResult: null,
  joinError: null,
  hunt: null,
  collab: null,
  contest: { mine: null, tally: null },
  watched: false,
  spectate: null,
  submitted: null,
  reveal: null,
  gallery: null,
  chat: [],
  reactions: [],
  toasts: [],
  submittedRound: 0,

  dismissToast: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
  dropReaction: (id) => set((state) => ({ reactions: state.reactions.filter((r) => r.id !== id) })),
  markSubmitted: (round) => set({ submittedRound: round }),
  rememberSubmission: (submitted) => set({ submitted }),
  reset: () =>
    set({
      room: null,
      playerId: null,
      assignment: null,
      guessTarget: null,
      guessResult: null,
      joinError: null,
      hunt: null,
      collab: null,
      contest: { mine: null, tally: null },
      watched: false,
      spectate: null,
      submitted: null,
      reveal: null,
      gallery: null,
      chat: [],
      reactions: [],
      submittedRound: 0,
    }),
}));

function pushToast(
  key: TranslationKey,
  tone: ToastState['tone'],
  params?: Readonly<Record<string, string | number>>,
): void {
  notify(key, tone, params === undefined ? {} : { params });
}

/**
 * Emet une bulle depuis n'importe ou dans le client.
 *
 * `pushToast` est reserve aux reactions aux messages du serveur ; celle-ci
 * ouvre le meme canal aux actions purement locales (le partage d'une retouche,
 * qui passe par HTTP et non par la socket). Le plafond est pose ICI, a la
 * source : n'en afficher que trois en gardant les autres en memoire ferait une
 * FILE, et la pile mettrait des dizaines de secondes a se vider en montrant
 * des evenements perimes.
 */
export function notify(
  key: TranslationKey,
  tone: ToastState['tone'],
  options: {
    readonly params?: Readonly<Record<string, string | number>>;
    readonly link?: ToastState['link'];
  } = {},
): void {
  const toast: ToastState = {
    id: nextId++,
    key,
    tone,
    ...(options.params === undefined ? {} : { params: options.params }),
    ...(options.link === undefined ? {} : { link: options.link }),
  };
  useRoomStore.setState((state) => ({ toasts: [...state.toasts, toast].slice(-MAX_TOASTS) }));
}

/**
 * Annonce les arrivees et les departs.
 *
 * Compare deux instantanes successifs : le serveur n'envoie pas d'evenement
 * dedie, mais la liste des joueurs EST l'evenement. Rien n'est annonce sur le
 * premier instantane, sinon entrer dans un salon de cinq personnes ferait
 * defiler cinq bulles pour rien.
 */
function announceRoster(previous: RoomSnapshot | null, next: RoomSnapshot): void {
  if (previous === null || previous.code !== next.code) return;

  const before = new Map(previous.players.map((player) => [player.id, player]));
  const after = new Map(next.players.map((player) => [player.id, player]));

  for (const player of next.players) {
    if (before.has(player.id)) continue;
    pushToast(player.bot ? 'toast.botJoined' : 'toast.playerJoined', 'info', {
      name: player.nickname,
    });
  }
  for (const player of previous.players) {
    if (after.has(player.id)) continue;
    // Le bot qui cede sa place a un joueur reel n'est pas un depart : c'est le
    // fonctionnement normal de la partie rapide, l'annoncer serait du bruit.
    if (player.bot) continue;
    pushToast('toast.playerLeft', 'info', { name: player.nickname });
  }
}

function applyServerMessage(message: ServerMessage): void {
  switch (message.type) {
    case 'welcome':
      useRoomStore.setState({ playerId: message.playerId });
      return;

    case 'transfer': {
      // Le serveur nous envoie sur une autre table de partie rapide, ou du
      // monde attend. On y entre comme si l'on avait clique sur « Partie
      // rapide » : meme chemin, meme pseudo. Le jeton de la table precedente
      // est jete, sinon la reconnexion nous y ramenerait.
      const nickname = useUiStore.getState().nickname;
      connection.clearSession();
      useRoomStore.getState().reset();
      connection.join(nickname, { code: message.code });
      return;
    }

    case 'room': {
      const previous = useRoomStore.getState().room;
      const phaseChanged = previous?.phase !== message.room.phase;
      const roundChanged = previous?.round !== message.room.round;

      announceRoster(previous, message.room);

      useRoomStore.setState({ room: message.room, joinError: null });

      // Les donnees de phase ne survivent pas a un changement de phase : sinon
      // un ecran pourrait afficher la cible de la manche precedente.
      if (phaseChanged || roundChanged) {
        const phase = message.room.phase;
        useRoomStore.setState({
          // La duree de vie de chaque donnee de phase est enoncee et testee
          // dans shared/phase.ts : c'est une regle de jeu, pas un detail de ce
          // store, et elle s'est deja revelee piegeuse sur COLLECTING.
          ...(keepsAssignment(phase) ? {} : { assignment: null }),
          ...(keepsGuessTarget(phase) ? {} : { guessTarget: null, guessResult: null }),
          ...(keepsHunt(phase) ? {} : { hunt: null }),
          // Le mode spectateur du dessin ne survit pas a la phase de dessin :
          // ensuite, c'est la chasse que le spectateur regarde, comme tout le
          // monde. `watched` retombe avec elle, sans attendre le message.
          ...(keepsAssignment(phase) ? {} : { spectate: null, watched: false }),
          ...(phase === 'REVEAL' ? {} : { reveal: null }),
          ...(phase === 'GAME_END' ? {} : { gallery: null }),
        });
      }
      return;
    }

    case 'assignment': {
      // RECONNEXION EN PLEIN DESSIN : le serveur renvoie le tableau en cours,
      // et c'est le MEME. Le reposer donnait un objet neuf, donc un plateau
      // remonte de zero : tous les traits deja poses disparaissaient, et un
      // dessin deja rendu redevenait modifiable a l'ecran. Meme regle que
      // pour les moteurs de chasse : on compare des valeurs, pas des objets.
      const current = useRoomStore.getState().assignment;
      if (
        current !== null &&
        current.round === message.round &&
        current.file === message.painting.file &&
        current.zone.x === message.zone.x &&
        current.zone.y === message.zone.y &&
        current.zone.width === message.zone.width &&
        current.zone.height === message.zone.height
      ) {
        return;
      }
      useRoomStore.setState({
        assignment: {
          round: message.round,
          file: message.painting.file,
          zone: message.zone,
          palette: message.palette,
          expected: message.layers ?? 0,
          layers: [],
          layerPalettes: message.layerPalettes ?? [],
        },
        submittedRound: 0,
      });
      return;
    }

    case 'collab':
      // Les tableaux arrivent d'un coup, les retouches suivent en trames. On
      // repart d'un etat neuf : une revanche ne doit pas herite des voix ni du
      // depouillement de la partie precedente.
      useRoomStore.setState({
        collab: { boards: message.boards, drawings: {}, mine: {}, tally: null },
      });
      return;

    case 'collabVotes':
      useRoomStore.setState((state) =>
        state.collab === null ? {} : { collab: { ...state.collab, tally: message.votes } },
      );
      return;

    case 'guessTarget':
      useRoomStore.setState({
        guessTarget: {
          round: message.round,
          drawerId: message.drawerId,
          file: message.painting.file,
          zone: message.zone,
          palette: message.palette,
          slot: message.slot,
          drawing: null,
        },
        guessResult: null,
      });
      return;

    case 'guessResult':
      useRoomStore.setState({
        guessResult: {
          found: message.found,
          zone: message.zone,
          distance: message.distance,
          guesserPoints: message.guesserPoints,
          drawerPoints: message.drawerPoints,
        },
      });
      return;

    case 'watched':
      useRoomStore.setState({ watched: message.watching });
      return;

    case 'spectate':
      useRoomStore.setState((state) => ({
        spectate: {
          target: message.target,
          round: message.round,
          file: message.painting?.file ?? null,
          zone: message.zone,
          palette: message.palette,
          slot: message.slot,
          // Ce message revient a chaque fois que la palette de l'auteur
          // s'enrichit. Repartir de zero effacerait le dessin deja a l'ecran,
          // le temps du prochain instantane : un clignotement toutes les deux
          // secondes. On ne repart a vide qu'en changeant de cible.
          drawing: state.spectate?.slot === message.slot ? state.spectate.drawing : null,
        },
      }));
      return;

    case 'hunt':
      useRoomStore.setState({
        hunt: {
          round: message.round,
          file: message.painting.file,
          targets: message.targets,
          drawings: {},
          found: {},
          foundOrder: [],
        },
        // Nouvelle manche : le vote precedent et son depouillement n'ont plus
        // cours. Sans cette remise a zero, la manche 2 s'ouvrait avec le
        // resultat de la manche 1 deja affiche.
        contest: { mine: null, tally: null },
      });
      return;

    case 'voted':
      // Le tableau n'est la qu'en collaboratif, ou l'on vote une fois par
      // tableau. Le concours n'en a qu'un, et garde donc une voix unique.
      if (message.board !== undefined) {
        const board = message.board;
        useRoomStore.setState((state) =>
          state.collab === null
            ? {}
            : { collab: { ...state.collab, mine: { ...state.collab.mine, [board]: message.slot } } },
        );
        return;
      }
      useRoomStore.setState((state) => ({ contest: { ...state.contest, mine: message.slot } }));
      return;

    case 'votes':
      useRoomStore.setState((state) => ({
        contest: {
          ...state.contest,
          tally: Object.fromEntries(message.tally.map((entry) => [entry.slot, entry.count])),
        },
      }));
      return;

    case 'found': {
      // Une trouvaille est l'evenement le plus fort de la manche : elle merite
      // d'etre dite, et de nommer l'auteur dont la cachette vient de tomber.
      const room = useRoomStore.getState().room;
      const self = useRoomStore.getState().playerId;
      const finder = room?.players.find((player) => player.id === message.finderId);
      const drawer = room?.players.find((player) => player.id === message.drawerId);
      pushToast(
        message.finderId === self ? 'toast.youFound' : 'toast.playerFound',
        'info',
        {
          finder: finder?.nickname ?? '?',
          drawer: drawer?.nickname ?? '?',
          points: message.finderPoints,
        },
      );

      useRoomStore.setState((state) =>
        state.hunt === null
          ? {}
          : {
              hunt: {
                ...state.hunt,
                found: {
                  ...state.hunt.found,
                  [message.slot]: { drawerId: message.drawerId, finderId: message.finderId },
                },
                // Une trouvaille rejouee a la reconnexion ne doit pas compter
                // deux fois : le classement en dependrait.
                foundOrder: state.hunt.foundOrder.includes(message.slot)
                  ? state.hunt.foundOrder
                  : [...state.hunt.foundOrder, message.slot],
              },
            },
      );
      return;
    }

    case 'eliminated': {
      // La sortie d'un joueur doit s'ANNONCER : le snapshot la dit deja, mais un
      // nom qui se barre dans la colonne des scores passe inapercu au milieu
      // d'une manche.
      const room = useRoomStore.getState().room;
      const player = room?.players.find((candidate) => candidate.id === message.playerId);
      // Plus de points dans ce mode : la bulle ne dit que le nom. Un
      // « 0 pts » accole a une sortie se lirait comme la RAISON de la sortie.
      pushToast('hunt.eliminatedRound', 'info', { name: player?.nickname ?? '?' });
      return;
    }

    case 'reveal':
      useRoomStore.setState({
        reveal: { round: message.round, items: message.items, drawings: {} },
      });
      return;

    case 'gallery':
      useRoomStore.setState((state) => ({
        // Une reconnexion en GAME_END renvoie la galerie : on garde les blobs
        // deja recus plutot que de tout retelecharger.
        gallery: { items: message.items, drawings: state.gallery?.drawings ?? {} },
      }));
      return;

    case 'chat':
      useRoomStore.setState((state) => ({
        // Plafond : une partie longue ne doit pas faire enfler la memoire.
        chat: [
          ...state.chat.slice(-199),
          {
            id: nextId++,
            playerId: message.playerId,
            nickname: message.nickname,
            text: message.text,
            at: message.at,
          },
        ],
      }));
      return;

    case 'reaction':
      useRoomStore.setState((state) => ({
        reactions: [
          ...state.reactions,
          { id: nextId++, playerId: message.playerId, emoji: message.emoji },
        ],
      }));
      return;

    case 'error':
      pushToast(errorKey(message.code), 'error');
      // Refus alors qu'on n'est dans aucun salon : c'est un echec d'entree, et
      // l'ecran doit pouvoir le dire plutot que d'attendre indefiniment.
      if (useRoomStore.getState().room === null) {
        useRoomStore.setState({ joinError: message.code });
      }
      return;

    default:
      return;
  }
}

/** Branche le store sur la connexion. Appele une fois au demarrage. */
export function bindRoomStore(): void {
  connection.on('status', (status) => useRoomStore.setState({ status }));
  connection.on('message', applyServerMessage);

  connection.on('drawing', (frame) => {
    if (frame.tag === DRAWING_TAG.guessTarget) {
      useRoomStore.setState((state) =>
        state.guessTarget === null || state.guessTarget.slot !== frame.slot
          ? {}
          : { guessTarget: { ...state.guessTarget, drawing: frame.drawing } },
      );
      return;
    }

    if (frame.tag === DRAWING_TAG.preview) {
      // Instantane du joueur regarde. On ignore ce qui ne concerne pas la cible
      // courante : un dernier apercu peut arriver apres un changement de cible.
      useRoomStore.setState((state) =>
        state.spectate === null || state.spectate.slot !== frame.slot
          ? {}
          : { spectate: { ...state.spectate, drawing: frame.drawing } },
      );
      return;
    }

    if (frame.tag === DRAWING_TAG.collabBoard) {
      useRoomStore.setState((state) =>
        state.collab === null
          ? {}
          : { collab: { ...state.collab, drawings: { ...state.collab.drawings, [frame.slot]: frame.drawing } } },
      );
      return;
    }

    if (frame.tag === DRAWING_TAG.collabBase) {
      // Une couche deja posee sur le tableau qu'on vient de recevoir. Elles
      // arrivent dans l'ordre, mais on les range par SLOT : l'ordre d'arrivee
      // d'un lot de trames n'est pas garanti, et peindre la troisieme
      // par-dessus la premiere donnerait un fond faux.
      useRoomStore.setState((state) => {
        if (state.assignment === null) return {};
        const layers = [...state.assignment.layers];
        layers[frame.slot] = frame.drawing;
        return { assignment: { ...state.assignment, layers } };
      });
      return;
    }

    if (frame.tag === DRAWING_TAG.hunt) {
      useRoomStore.setState((state) =>
        state.hunt === null
          ? {}
          : { hunt: { ...state.hunt, drawings: { ...state.hunt.drawings, [frame.slot]: frame.drawing } } },
      );
      return;
    }

    if (frame.tag === DRAWING_TAG.gallery) {
      useRoomStore.setState((state) => ({
        gallery: {
          items: state.gallery?.items ?? [],
          drawings: { ...(state.gallery?.drawings ?? {}), [frame.slot]: frame.drawing },
        },
      }));
      return;
    }

    useRoomStore.setState((state) =>
      state.reveal === null
        ? {}
        : { reveal: { ...state.reveal, drawings: { ...state.reveal.drawings, [frame.slot]: frame.drawing } } },
    );
  });
}

// ---------------------------------------------------------------------------
// Selecteurs
// ---------------------------------------------------------------------------

export function useIsHost(): boolean {
  return useRoomStore((state) => state.room !== null && state.room.hostId === state.playerId);
}

export function useSelf(): RoomSnapshot['players'][number] | null {
  return useRoomStore(
    (state) => state.room?.players.find((player) => player.id === state.playerId) ?? null,
  );
}

export function playerById(room: RoomSnapshot | null, id: string | null) {
  if (room === null || id === null) return null;
  return room.players.find((player) => player.id === id) ?? null;
}
